import { useState } from "react";
import {
  Mail,
  Phone,
  MapPin,
  Loader2,
  MessageCircle,
  PhoneCall,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import BrandLogo from "../components/BrandLogo";

const OFFICE_NUMBER = "+91 94141 56086";

const IT_NUMBER = "+91 84420 97839";
const OFFICE_WHATSAPP = "919414156086";
const OFFICE_WA_LINK = `https://wa.me/${OFFICE_WHATSAPP}?text=${encodeURIComponent(
  "Hello Oswal Sabha Professional Forum, I need help with membership, activities, or admin support.",
)}`;

const whatsappNumberFor = (number) => number.replace(/\D/g, "");

const leaders = [
  {
    title: "President",
    name: "CA Dinesh Kothari",
    number: OFFICE_NUMBER,
    whatsappText:
      "Hello President, I need help with the forum contact details.",
  },
  {
    title: "General Secretary",
    name: "Mr. Dinesh Babel",
    number: OFFICE_NUMBER,
    whatsappText:
      "Hello General Secretary, I need help with the forum contact details.",
  },
  {
    title: "Treasurer",
    name: "Mr. Jatin Nagori",
    number: OFFICE_NUMBER,
    whatsappText:
      "Hello Treasurer, I need help with the forum contact details.",
  },
  {
    title: "IT Team",
    name: "Er. Gauransh Jaroli",
    number: IT_NUMBER,
    whatsappText:
      "Hello IT Team, I need help with the website or contact support.",
  },
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Message received. We'll respond within 48 hours.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="container-px pt-16 md:pt-24 pb-12">
        <div className="max-w-3xl">
          <BrandLogo className="mb-6" />
          <div className="overline mb-6">Contact</div>
          <h1 className="font-heading text-5xl md:text-6xl font-light tracking-tight text-ink leading-[1.05]">
            Write to the <span className="italic text-brand">forum</span>.
          </h1>
          <p className="mt-6 text-lg text-ink-soft leading-relaxed">
            For membership queries, welfare applications, partnerships or press
            — our council reads every message.
          </p>
        </div>
      </section>

      <section className="container-px pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {leaders.map((leader, i) => (
                <div
                  key={leader.name}
                  className="card-premium"
                  data-testid={`leader-card-${i}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-brand/10 text-brand flex items-center justify-center shrink-0">
                      <Phone className="w-5 h-5" strokeWidth={1.5} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="overline mb-1">{leader.title}</div>
                      <div className="font-heading text-xl font-medium text-ink">
                        {leader.name}
                      </div>
                      <a
                        href={`tel:${leader.number.replace(/\s+/g, "")}`}
                        className="mt-2 inline-flex items-center gap-2 text-sm font-medium text-ink-soft hover:text-ink transition"
                      >
                        <PhoneCall className="h-4 w-4" />
                        {leader.number}
                      </a>
                    </div>
                  </div>

                  <div className="mt-5 flex flex-col sm:flex-row gap-3">
                    <a
                      href={`tel:${leader.number.replace(/\s+/g, "")}`}
                      className="inline-flex items-center justify-center gap-2 rounded-full border border-black/10 px-4 py-3 text-sm font-medium text-ink hover:bg-black/5 transition"
                    >
                      <PhoneCall className="h-4 w-4" />
                      Call now
                    </a>
                    <a
                      href={`https://wa.me/${whatsappNumberFor(leader.number)}?text=${encodeURIComponent(leader.whatsappText)}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center justify-center gap-2 rounded-full bg-whatsapp px-4 py-3 text-sm font-medium text-white hover:bg-whatsapp-hover transition"
                    >
                      <MessageCircle className="h-4 w-4" />
                      Chat on WhatsApp
                    </a>
                  </div>
                </div>
              ))}
            </div>

            <form
              onSubmit={submit}
              className="card-premium space-y-5"
              data-testid="contact-form"
            >
              <div className="flex items-center gap-3 pb-1">
                <BrandLogo compact imageClassName="w-9 h-9" />
                <div>
                  <div className="font-heading text-base font-semibold text-ink">
                    Contact the forum
                  </div>
                  <div className="text-xs uppercase tracking-[0.22em] text-brand-secondary">
                    We read every message
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  className="input-premium"
                  placeholder="Full name"
                  required
                  value={form.name}
                  onChange={change("name")}
                  data-testid="contact-form-name"
                />
                <input
                  className="input-premium"
                  placeholder="Email"
                  type="email"
                  required
                  value={form.email}
                  onChange={change("email")}
                  data-testid="contact-form-email"
                />
              </div>
              <input
                className="input-premium"
                placeholder="Subject"
                required
                value={form.subject}
                onChange={change("subject")}
                data-testid="contact-form-subject"
              />
              <textarea
                className="input-premium min-h-[160px]"
                placeholder="How can we help?"
                required
                value={form.message}
                onChange={change("message")}
                data-testid="contact-form-message"
              />
              <button
                type="submit"
                disabled={submitting}
                className="btn-primary w-full md:w-auto"
                data-testid="contact-form-submit"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Sending…
                  </>
                ) : (
                  "Send message"
                )}
              </button>
            </form>
          </div>

          <div className="lg:col-span-5 space-y-6 lg:sticky lg:top-28">
            <div className="card-premium">
              <div className="overline mb-3">Contact us</div>
              <div className="space-y-4 text-sm text-ink-soft">
                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-brand shrink-0 mt-0.5" />
                  <div>
                    <div className="font-medium text-ink">
                      Oswal Bhawan, Mukherji Chowk
                    </div>
                    <div>Udaipur 313001, Rajasthan</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Mail className="h-5 w-5 text-brand shrink-0 mt-0.5" />
                  <div>
                    <div className="font-medium text-ink">
                      oswalsabhaprofessionalforum@gmail.com
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Phone className="h-5 w-5 text-brand shrink-0 mt-0.5" />
                  <div>
                    <div className="font-medium text-ink">{OFFICE_NUMBER}</div>
                  </div>
                </div>
              </div>

              <div className="mt-5 flex flex-col sm:flex-row gap-3">
                <a
                  href={`tel:${OFFICE_NUMBER.replace(/\s+/g, "")}`}
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-black/10 px-4 py-3 text-sm font-medium text-ink hover:bg-black/5 transition"
                >
                  <PhoneCall className="h-4 w-4" />
                  Call now
                </a>
                <a
                  href={OFFICE_WA_LINK}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-whatsapp px-4 py-3 text-sm font-medium text-white hover:bg-whatsapp-hover transition"
                >
                  <MessageCircle className="h-4 w-4" />
                  Chat on WhatsApp
                </a>
              </div>
            </div>

            <div className="card-premium overflow-hidden">
              <div className="overline mb-3">Find us</div>
              <div className="overflow-hidden rounded-2xl border border-black/10 bg-white shadow-sm">
                <div className="px-4 pt-4 pb-3 border-b border-black/5">
                  <div className="text-sm font-semibold text-black">
                    Oswal Bhawan, Mukherji Chowk
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Udaipur 313001, Rajasthan
                  </div>
                </div>

                <div className="aspect-[4/3] w-full bg-gray-100">
                  <iframe
                    title="Oswal Sabha Professional Forum location map"
                    src="https://www.google.com/maps?q=Oswal%20Bhawan%2C%20Mukherji%20Chowk%2C%20Udaipur%20313001&output=embed"
                    className="h-full w-full"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>

                <div className="px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <span className="text-xs text-gray-500">
                    Tap to open in Google Maps
                  </span>
                  <a
                    href="https://www.google.com/maps?q=Oswal%20Bhawan%2C%20Mukherji%20Chowk%2C%20Udaipur%20313001"
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs font-medium text-orange-600 hover:text-orange-700"
                  >
                    Open maps →
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
